import React, { useRef, useState, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import { Button, Stack, Skeleton, CircularProgress } from '@mui/material'
import { DiscountLabel, ProductName, BrandName, StockLabel } from '../styledComponents/StyledItemComponents'
import DeleteIcon from '@mui/icons-material/Delete'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Currency } from 'react-intl-number-format'
import StyledLink from '../StyledLink'
import { useAppContext } from '../../context/AppProvider'
import PropTypes from 'prop-types'
import { useFavorites } from '../../hooks/useFavorites'
import { useCart } from '../../hooks/useCart'
import { debounce } from 'lodash'
import { SHIPPING_PRICE, getCollectionBadge, getDisplayBrandName, getEffectivePrice, getStockLabel } from '../../utils/itemPresentation'

export default function MediaControlCard ({ item }) {
  const { state } = useAppContext()
  const { removeItemFromFavorites } = useFavorites()
  const { updateCartItem } = useCart()
  const theme = useTheme()
  const itemData = useRef(item)
  const [addingToCart, setAddingToCart] = useState(false)
  const collectionBadge = getCollectionBadge(item)
  const finalPrice = getEffectivePrice(item)
  const stockLabel = getStockLabel(item)
  const displayBrandName = getDisplayBrandName(item)

  const debouncedCartAction = useCallback(debounce((action, data) => {
    action(data)
    setAddingToCart(false)
  }, 1000, {
    leading: false,
    trailing: true
  }), [])

  const handleAddToCart = () => {
    setAddingToCart(true)
    debouncedCartAction(updateCartItem, itemData)
  }

  return (
    <Card
      sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'space-between' }, width: { xs: '250px', sm: 'auto' }, minHeight: { xs: '470px', sm: '250px', md: '160px' }, marginTop: '20px', padding: '0 30px', flexDirection: { xs: 'column', sm: 'row' } }}
    >
      <Box display='flex' sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        {state.favorites.loading
          ? (
            <Box sx={{ minWidth: '220px', minHeight: '450px' }}>
              <Skeleton variant='rectangular' width={210} height={118} />
              <Box>
                <Skeleton variant='text' sx={{ width: '50%', fontSize: '22px' }} />
                <Skeleton animation='wave' sx={{ width: '75%' }} />
                <Skeleton animation='wave' sx={{ width: '75%' }} />
                <Skeleton variant='text' sx={{ width: '50%', fontSize: '22px' }} />
              </Box>
            </Box>
            )
          : (
            <>
              <Box sx={{ position: 'relative', minWidth: '150px', display: 'flex', justifyContent: 'center' }}>
                {item.brand?.logo
                  ? (
                    <Box
                      component='img'
                      src={item.brand.logo}
                      alt={item.brand?.name || 'Brand logo'}
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        width: '78px',
                        height: '26px',
                        objectFit: 'contain',
                        objectPosition: 'left center',
                        zIndex: 2,
                        backgroundColor: 'rgba(255,255,255,0.92)',
                        borderRadius: '8px',
                        px: 0.75,
                        py: 0.25
                      }}
                    />
                    )
                  : null}
                <CardMedia
                  component='img'
                  sx={{ height: '100%', width: '150px', objectFit: 'contain' }}
                  image={item.cover}
                  alt='Product image'
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: '1' }}>
                  {item && (
                    <StyledLink to={{ pathname: `/items/${item._id}` }}>
                      <ProductName sx={{ fontSize: '14px', WebkitLineClamp: 2, textOverflow: 'ellipsis' }}>
                        {item.name}
                      </ProductName>
                    </StyledLink>
                  )}

                  {!item.brand?.logo && displayBrandName && (
                    <BrandName sx={{ fontSize: '13px', marginTop: '8px', marginBottom: '10px' }}>
                      {displayBrandName}
                    </BrandName>
                  )}
                  {collectionBadge && (
                    <DiscountLabel sx={{ top: 'auto', left: 0, marginBottom: '10px' }}>
                      {collectionBadge}
                    </DiscountLabel>
                  )}
                  <StockLabel sx={{ position: 'relative', left: '0px', paddingLeft: '10px' }}>
                    {stockLabel}
                  </StockLabel>
                  <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginTop: '10px' }}>
                    Transport: {SHIPPING_PRICE} lei
                  </Typography>
                </CardContent>
              </Box>
            </>)}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Stack sx={{ padding: '15px' }}>
          <Typography variant='h6' sx={{ color: theme.palette.accent.main, fontSize: '16px', fontWeight: 600 }}>
            <Currency locale='ro-RO' currency='RON'>
              {finalPrice}
            </Currency>
          </Typography>
          <Button
            variant='contained'
            size='small'
            color='accent'
            startIcon={addingToCart ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
            disabled={addingToCart}
            onClick={handleAddToCart}
            sx={{
              color: 'white',
              borderRadius: '18px',
              height: 'auto',
              textTransform: 'none',
              backgroundColor: theme.palette.accent.main,
              '&:hover': { backgroundColor: theme.palette.accent.light }
            }}
          >
            {addingToCart ? 'Adăugare...' : 'ADAUGĂ'}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<DeleteIcon />}
              color='success'
              onClick={() => removeItemFromFavorites(itemData)}
              sx={{ color: 'success', borderRadius: '16px', height: 'auto', marginTop: '10px', textTransform: 'none' }}
            >
              Șterge
            </Button>
          </Box>
        </Stack>
      </Box>
    </Card>
  )
}
MediaControlCard.propTypes = {
  item: PropTypes.oneOfType([PropTypes.object, PropTypes.number])
}
